import { FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';

/**
 * Props interface for the ConfirmDialog component
 */
interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Title of the dialog */
  title: string;
  /** Message to display in the dialog */
  message: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the dismiss/negative button (default: "Cancel") */
  cancelText?: string;
  /** Whether the action is destructive (will style the confirm button as error) */
  isDestructive?: boolean;
  /** Whether the action is currently loading */
  isLoading?: boolean;
  /** Text to show on confirm button during loading */
  loadingText?: string;
  /** Callback when the user confirms */
  onConfirm: () => void;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * A reusable confirmation dialog component used for confirming user actions.
 * Can be used for any type of confirmation (delete, save, etc.) across the application.
 *
 * Features:
 * - Customizable confirm button text
 * - Optional destructive action styling
 * - Accessible dialog with proper ARIA labels
 * - Consistent Material-UI styling
 *
 * @example
 * // For destructive actions like delete
 * <ConfirmDialog
 *   open={open}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   confirmText="Delete"
 *   isDestructive
 *   onConfirm={handleDelete}
 *   onClose={handleClose}
 * />
 *
 * @example
 * // For non-destructive actions like save
 * <ConfirmDialog
 *   open={open}
 *   title="Save Changes"
 *   message="Do you want to save your changes?"
 *   confirmText="Save"
 *   onConfirm={handleSave}
 *   onClose={handleClose}
 * />
 *
 * @component
 * @param {ConfirmDialogProps} props - Component props
 * @returns {JSX.Element} Rendered ConfirmDialog component
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  isLoading = false,
  loadingText,
  onConfirm,
  onClose,
}) => {
  const theme = useTheme();

  /**
   * Handles the confirmation action
   * Calls onConfirm and closes the dialog
   */
  const handleConfirm = (): void => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      sx={{ zIndex: theme.zIndex.modal + 100 }}
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText
          id="confirm-dialog-description"
          // Preserve newlines in plain-text messages (e.g. multi-line ProblemDetails.detail);
          // pre-line keeps \n as line breaks while still wrapping and collapsing other whitespace.
          sx={{ whiteSpace: 'pre-line' }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color={isDestructive ? 'error' : 'primary'}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
          autoFocus
        >
          {isLoading ? loadingText || confirmText : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
